import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface Folder {
  id: string;
  name: string;
  parent_id: string | null;
}

interface MoveFileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileIds: string[];
  folders: Folder[];
  onFileMoved: () => void;
}

export default function MoveFileDialog({
  open,
  onOpenChange,
  fileIds,
  folders,
  onFileMoved,
}: MoveFileDialogProps) {
  const [targetFolderId, setTargetFolderId] = useState<string>('');
  const [moving, setMoving] = useState(false);
  const { toast } = useToast();

  const handleMove = async () => {
    setMoving(true);
    try {
      const { error } = await supabase
        .from('data_room_files')
        .update({ folder_id: targetFolderId || null })
        .in('id', fileIds);

      if (error) throw error;

      toast({
        title: 'Files moved',
        description: `${fileIds.length} file(s) moved successfully`,
      });

      setTargetFolderId('');
      onOpenChange(false);
      onFileMoved();
    } catch (error) {
      console.error('Error moving files:', error);
      toast({
        title: 'Failed to move files',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setMoving(false);
    }
  };

  // Build a hierarchical folder list for display
  const buildFolderPath = (folderId: string): string => {
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return '';
    if (!folder.parent_id) return folder.name;
    const parentPath = buildFolderPath(folder.parent_id);
    return parentPath ? `${parentPath} / ${folder.name}` : folder.name;
  };

  const rootFolders = folders.filter((f) => !f.parent_id);
  const subFolders = folders.filter((f) => f.parent_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move {fileIds.length} File(s)</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="target-folder">Destination Folder</Label>
            <Select value={targetFolderId} onValueChange={setTargetFolderId} disabled={moving}>
              <SelectTrigger id="target-folder">
                <SelectValue placeholder="Select destination folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Root (No Folder)</SelectItem>
                {rootFolders.map((folder) => (
                  <SelectItem key={folder.id} value={folder.id}>
                    {folder.name}
                  </SelectItem>
                ))}
                {subFolders.length > 0 && (
                  <>
                    {subFolders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {buildFolderPath(folder.id)}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={moving}>
            Cancel
          </Button>
          <Button onClick={handleMove} disabled={moving}>
            {moving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Move Files
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
